import { ActionIcon, Badge, Button, Group, Menu, Text, Tooltip } from '@mantine/core';
import { Check, Languages, Palette } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useAuth from '@hooks/use-auth';
import { colors as palettes, SELECTABLE_THEME_MODES } from '@constants/colors';
import { THEME_LABEL_KEY } from '@constants/theme-labels';
import { LANGUAGES } from '@constants/translations';
import { useLanguage } from '@contexts/LanguageContext';
import { useTheme } from '@contexts/ThemeContext';
import { getCcnCompany, getSessionEmail } from '@utils/auth-session';

/**
 * The only chrome a CCN (CUBEforall) session gets: who they are, the two
 * personal settings that apply to them (language, theme), and a way
 * out. No sidebar, no module menus, no settings page — none of JARVIS's
 * PPJK / CEISA settings apply to a CCN user.
 */
export default function CcnTopBar() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { colors, mode, toggleTheme } = useTheme();
  const { language, setLanguage, meta, t } = useLanguage();
  const company = getCcnCompany();
  const email = getSessionEmail();

  // Ends the JARVIS session only. The user came from the CUBEforall portal
  // and goes back to it; signing them out of CCN as well is not ours to do,
  // and in popup mode the SDK can only do it by flashing a CCN window.
  const handleLogout = async () => {
    await logout(() => navigate('/auth', { replace: true }));
  };

  return (
    <Group
      justify="space-between"
      px="md"
      py="xs"
      wrap="nowrap"
      style={{
        borderBottom: `1px solid ${colors.border ?? 'rgba(0,0,0,0.08)'}`,
        // Solid, like the sidebar in the redesigned workspace (no palette has a 'surface').
        backgroundColor: colors.sidebar ?? colors.card,
      }}
    >
      <Group gap="sm" wrap="nowrap">
        <img src="/image/png/ccn.png" alt="CUBEforall by CCN" style={{ height: 24, width: 24, objectFit: 'contain' }} />
        <Text fw={700}>JARVIS</Text>
        <Badge variant="light" color="blue" size="sm">
          CUBEforall
        </Badge>
        <Text size="sm" c="dimmed" visibleFrom="sm">
          {t('ccn.product')}
        </Text>
      </Group>
      <Group gap="sm" wrap="nowrap">
        {company && (
          <Text size="sm" fw={500} visibleFrom="md">
            {company}
          </Text>
        )}
        {email && (
          <Text size="sm" c="dimmed" visibleFrom="sm">
            {email}
          </Text>
        )}
        <Menu position="bottom-end" offset={6}>
          <Menu.Target>
            <Tooltip label={t('ccn.language')} withArrow>
              <ActionIcon variant="subtle" aria-label={t('ccn.language')} style={{ color: colors.text }}>
                <span style={{ fontSize: 15, lineHeight: 1 }}>{meta.flag}</span>
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
            <Menu.Label>
              <Group gap={6}>
                <Languages size={12} /> {t('ccn.language')}
              </Group>
            </Menu.Label>
            {LANGUAGES.map((lang) => (
              <Menu.Item
                key={lang.code}
                leftSection={<span style={{ fontSize: 15 }}>{lang.flag}</span>}
                onClick={() => setLanguage(lang.code)}
                style={{ color: colors.text, fontWeight: language === lang.code ? 600 : 400 }}
              >
                {lang.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
        <Menu position="bottom-end" offset={6}>
          <Menu.Target>
            <Tooltip label={t('sidebar.theme')} withArrow>
              <ActionIcon variant="subtle" aria-label={t('sidebar.theme')} style={{ color: colors.text }}>
                <Palette size={16} />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
            <Menu.Label>
              <Group gap={6}>
                <Palette size={12} /> {t('sidebar.theme')}
              </Group>
            </Menu.Label>
            {SELECTABLE_THEME_MODES.map((option) => {
              const palette = (palettes as Record<string, Record<string, string | undefined>>)[option] ?? {};
              const selected = option === mode;
              return (
                <Menu.Item
                  key={option}
                  onClick={() => toggleTheme(option)}
                  leftSection={
                    <span
                      aria-hidden
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        width: 30,
                        height: 16,
                        borderRadius: 4,
                        overflow: 'hidden',
                        border: `1px solid ${colors.border}`,
                      }}
                    >
                      <span style={{ backgroundColor: palette.sidebar ?? palette.background }} />
                      <span style={{ backgroundColor: palette.accent }} />
                      <span style={{ backgroundColor: palette.background }} />
                    </span>
                  }
                  rightSection={selected ? <Check size={14} /> : null}
                  style={{ color: colors.text, fontWeight: selected ? 600 : 400 }}
                >
                  {t(THEME_LABEL_KEY[option] ?? 'sidebar.theme')}
                </Menu.Item>
              );
            })}
          </Menu.Dropdown>
        </Menu>
        <Button size="xs" variant="subtle" onClick={handleLogout}>
          {t('ccn.logout')}
        </Button>
      </Group>
    </Group>
  );
}
